// Foto do prontuário entra, ficha preenchida sai — com você conferindo no meio.
//
// A conferência não é formalidade: leitura de foto erra dígito, e "Cr 2,47"
// virando "Cr 247" numa ficha de saúde não é um erro qualquer. O modelo propõe;
// quem aplica é a pessoa, item por item, podendo corrigir antes.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  MARCADORES, acharMarcador, hojeISO, formatarData, formatarDataHora,
} from '@medicov1/shared';
import { chamar, ErroApi } from '../api';
import { Ditar } from '../componentes/Ditar';
import './importar.css';

interface ExameLido {
  marcador: string; valorTexto: string; valor: number | null;
  coletadoEm: string | null; reconhecido: boolean;
  rotulo: string | null; unidade: string | null;
}
interface ProcedimentoLido { nome: string; data: string | null; resultado: string | null }
interface MedicacaoLida { nome: string; dose: string | null; via: string | null; inicioEm: string | null }
interface CulturaLida { material: string; coletadoEm: string | null; resultado: string | null }

interface Extracao {
  tipo: string;
  dataDoRegistro: string | null;
  exames: ExameLido[];
  procedimentos: ProcedimentoLido[];
  medicacoes: MedicacaoLida[];
  culturas: CulturaLida[];
  resumo: string | null;
  observacoes: string | null;
}

interface DocumentoDTO {
  id: string; titulo: string; tipoMime: string; bytes: number;
  situacao: 'recebido' | 'lendo' | 'lido' | 'aplicado' | 'erro';
  falha: string | null; modelo: string | null;
  criadoEm: string; aplicadoEm: string | null;
  extracao: Extracao | null;
}

const BASE_API = import.meta.env.VITE_API_URL ?? '/api';

// Fuso de Brasília, do módulo compartilhado. Ver packages/shared/src/tempo.ts.
const hoje = () => hojeISO();
const dataBr = formatarData;
const quandoBr = formatarDataHora;
const emKb = (bytes: number) => `${Math.round(bytes / 1024)} KB`;

/** O que a pessoa marcou para aplicar, já com as correções dela. */
interface Marcado {
  exames: Record<number, { marcador: string; valor: string; coletadoEm: string }>;
  procedimentos: Record<number, { nome: string; data: string; resultado: string }>;
  medicacoes: Record<number, { nome: string; inicioEm: string }>;
  culturas: Record<number, { material: string; coletadoEm: string; resultado: string }>;
  anotacao: boolean;
}

const vazio = (): Marcado => ({ exames: {}, procedimentos: {}, medicacoes: {}, culturas: {}, anotacao: false });

interface Props {
  pacienteId: string;
  aoAplicar: () => void;
  aoVoltar: () => void;
}

export function Importar({ pacienteId, aoAplicar, aoVoltar }: Props) {
  const [documentos, setDocumentos] = useState<DocumentoDTO[]>([]);
  const [leituraOk, setLeituraOk] = useState<{ disponivel: boolean; modelo: string | null } | null>(null);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [titulo, setTitulo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [lendo, setLendo] = useState<string | null>(null);
  const [aberto, setAberto] = useState<DocumentoDTO | null>(null);
  const [marcado, setMarcado] = useState<Marcado>(vazio());
  const [erro, setErro] = useState('');
  const [feito, setFeito] = useState('');
  // O documento aberto para ver. O laudo fotografado É o documento: pode não
  // haver segunda chance de fotografar.
  const [vendo, setVendo] = useState<DocumentoDTO | null>(null);

  const carregar = useCallback(async () => {
    try {
      const [docs, disp] = await Promise.all([
        chamar<DocumentoDTO[]>(`/documentos?pacienteId=${pacienteId}`),
        chamar<{ disponivel: boolean; modelo: string | null }>('/documentos/leitura-disponivel'),
      ]);
      setDocumentos(docs);
      setLeituraOk(disp);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui carregar os documentos');
    }
  }, [pacienteId]);

  useEffect(() => { void carregar(); }, [carregar]);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!arquivo) return;
    setEnviando(true);
    setErro('');
    try {
      // Upload é multipart, não passa pelo cliente JSON da API.
      const corpo = new FormData();
      corpo.append('pacienteId', pacienteId);
      corpo.append('titulo', titulo.trim() || arquivo.name);
      corpo.append('arquivo', arquivo);

      const resposta = await fetch(`${BASE_API}/documentos`, {
        method: 'POST', body: corpo, credentials: 'include',
      });
      const json = await resposta.json().catch(() => null);
      if (!resposta.ok) throw new Error(json?.message ?? 'não consegui enviar o arquivo');

      setArquivo(null);
      setTitulo('');
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falhou o envio');
    } finally {
      setEnviando(false);
    }
  }

  async function ler(doc: DocumentoDTO) {
    setLendo(doc.id);
    setErro('');
    try {
      const lido = await chamar<DocumentoDTO>(`/documentos/${doc.id}/ler`, { metodo: 'POST' });
      await carregar();
      abrirParaConferir(lido);
    } catch (e) {
      setErro(
        e instanceof ErroApi && e.codigo === 'sem_chave_openrouter'
          ? 'Falta a chave do OpenRouter no .env. Sem ela a leitura automática não funciona — mas você pode lançar à mão pela Ficha.'
          : e instanceof Error ? e.message : 'não consegui ler o documento',
      );
      await carregar();
    } finally {
      setLendo(null);
    }
  }

  /** Abre a conferência com tudo que foi reconhecido já marcado. */
  function abrirParaConferir(doc: DocumentoDTO) {
    const e = doc.extracao;
    if (!e) return;
    const inicial = vazio();
    const dataPadrao = e.dataDoRegistro ?? hoje();

    e.exames.forEach((x, i) => {
      if (!x.reconhecido || x.valor === null) return; // o resto entra desmarcado
      inicial.exames[i] = { marcador: x.marcador, valor: String(x.valor), coletadoEm: x.coletadoEm ?? dataPadrao };
    });
    e.procedimentos.forEach((x, i) => {
      inicial.procedimentos[i] = { nome: x.nome, data: x.data ?? dataPadrao, resultado: x.resultado ?? '' };
    });
    e.medicacoes.forEach((x, i) => {
      inicial.medicacoes[i] = { nome: x.nome, inicioEm: x.inicioEm ?? dataPadrao };
    });
    e.culturas.forEach((x, i) => {
      inicial.culturas[i] = { material: x.material, coletadoEm: x.coletadoEm ?? dataPadrao, resultado: x.resultado ?? '' };
    });
    inicial.anotacao = Boolean(e.resumo);

    setMarcado(inicial);
    setAberto(doc);
  }

  function alternar<K extends keyof Omit<Marcado, 'anotacao'>>(
    grupo: K, indice: number, valorInicial: Marcado[K][number],
  ) {
    setMarcado((m) => {
      const copia = { ...m, [grupo]: { ...m[grupo] } } as Marcado;
      if (indice in copia[grupo]) delete copia[grupo][indice];
      else copia[grupo][indice] = valorInicial;
      return copia;
    });
  }

  const editar = <K extends keyof Omit<Marcado, 'anotacao'>>(
    grupo: K, indice: number, campo: string, valor: string,
  ) =>
    setMarcado((m) => ({
      ...m,
      [grupo]: { ...m[grupo], [indice]: { ...m[grupo][indice], [campo]: valor } },
    }));

  async function aplicar() {
    if (!aberto?.extracao) return;
    setEnviando(true);
    setErro('');
    try {
      const corpo = {
        exames: Object.values(marcado.exames)
          .map((e) => ({ marcador: e.marcador, valor: Number(e.valor.replace(',', '.')), coletadoEm: e.coletadoEm }))
          .filter((e) => Number.isFinite(e.valor)),
        procedimentos: Object.values(marcado.procedimentos).map((p) => ({
          nome: p.nome, solicitadoEm: p.data, realizadoEm: p.data,
          ...(p.resultado ? { resultado: p.resultado } : {}), situacao: 'realizado' as const,
        })),
        medicacoes: Object.values(marcado.medicacoes).map((m) => ({ nome: m.nome, inicioEm: m.inicioEm })),
        culturas: Object.values(marcado.culturas).map((c) => ({
          material: c.material, coletadoEm: c.coletadoEm,
          ...(c.resultado ? { resultado: c.resultado } : {}),
        })),
        ...(marcado.anotacao && aberto.extracao.resumo ? { anotacao: aberto.extracao.resumo } : {}),
      };

      const r = await chamar<{ exames: number; procedimentos: number; medicacoes: number; culturas: number }>(
        `/documentos/${aberto.id}/aplicar`, { metodo: 'POST', corpo },
      );
      setFeito(
        `Lançado: ${r.exames} exames, ${r.procedimentos} imagem/procedimento, ` +
        `${r.medicacoes} medicações, ${r.culturas} culturas.`,
      );
      setAberto(null);
      await carregar();
      aoAplicar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'não consegui aplicar');
    } finally {
      setEnviando(false);
    }
  }

  if (vendo) {
    const ehPdf = vendo.tipoMime === 'application/pdf';
    const url = `${BASE_API}/documentos/${vendo.id}/arquivo`;
    return (
      <main className="importar">
        <header className="importar__topo">
          <div>
            <h1>{vendo.titulo}</h1>
            <p className="importar__nota">
              Guardado em {quandoBr(vendo.criadoEm)} · {emKb(vendo.bytes)}
            </p>
          </div>
          <button className="botao botao--voltar" onClick={() => setVendo(null)}>Voltar</button>
        </header>

        <div className="visor">
          {ehPdf ? (
            <object data={url} type="application/pdf" className="visor__pdf">
              <p className="vazio">
                Este navegador não abre PDF aqui dentro.{' '}
                <a href={url} target="_blank" rel="noreferrer">Abrir em outra aba</a>
              </p>
            </object>
          ) : (
            // Abrir em aba própria permite ampliar e girar, que é o que se faz
            // com foto de laudo tirada torta no corredor.
            <a href={url} target="_blank" rel="noreferrer" title="abrir em tamanho cheio">
              <img src={url} alt={vendo.titulo} className="visor__imagem" />
            </a>
          )}
        </div>

        <p className="importar__rodape">
          O arquivo fica guardado na máquina, fora do Git, e só abre para quem
          está com a sessão aberta. Toque na imagem para ver em tamanho cheio.
        </p>
      </main>
    );
  }

  // ------------------------------------------------------- tela de conferência
  if (aberto?.extracao) {
    const e = aberto.extracao;
    const total =
      Object.keys(marcado.exames).length + Object.keys(marcado.procedimentos).length +
      Object.keys(marcado.medicacoes).length + Object.keys(marcado.culturas).length;

    return (
      <main className="importar">
        <header className="importar__topo">
          <div>
            <h1>Conferir antes de lançar</h1>
            <p className="importar__nota">
              Isto é o que o modelo <b>leu</b> — ainda não é dado do sistema. Confira
              cada número contra a foto, corrija o que estiver errado e desmarque o
              que não quiser. Só o que ficar marcado será gravado.
            </p>
          </div>
          <button className="botao botao--fantasma" onClick={() => setAberto(null)}>Cancelar</button>
        </header>

        {erro && <p className="aviso-erro">{erro}</p>}

        {e.exames.length > 0 && (
          <section className="conf">
            <h2 className="conf__titulo">Laboratório ({e.exames.length} lidos)</h2>
            <ul className="conf__lista">
              {e.exames.map((x, i) => {
                const marcadoAqui = i in marcado.exames;
                const item = marcado.exames[i];
                return (
                  <li key={i} className={`conf__item${marcadoAqui ? ' conf__item--on' : ''}`}>
                    <label className="conf__check">
                      <input type="checkbox" checked={marcadoAqui}
                        disabled={!x.reconhecido || x.valor === null}
                        onChange={() => alternar('exames', i, {
                          marcador: x.marcador, valor: String(x.valor ?? ''),
                          coletadoEm: x.coletadoEm ?? e.dataDoRegistro ?? hoje(),
                        })} />
                      <span>{x.rotulo ?? x.marcador}</span>
                    </label>

                    {marcadoAqui && item ? (
                      <div className="conf__campos">
                        <input className="conf__valor" value={item.valor} inputMode="decimal"
                          onChange={(ev) => editar('exames', i, 'valor', ev.target.value)} />
                        <span className="conf__unidade">{x.unidade}</span>
                        <input className="conf__data" type="date" value={item.coletadoEm}
                          onChange={(ev) => editar('exames', i, 'coletadoEm', ev.target.value)} />
                      </div>
                    ) : (
                      <span className="conf__cru">
                        {x.valorTexto}
                        {!x.reconhecido && <b className="conf__alerta"> — "{x.marcador}" não está no catálogo</b>}
                        {x.reconhecido && x.valor === null && <b className="conf__alerta"> — número ilegível</b>}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {e.procedimentos.length > 0 && (
          <section className="conf">
            <h2 className="conf__titulo">Imagem e procedimentos</h2>
            <ul className="conf__lista">
              {e.procedimentos.map((x, i) => {
                const on = i in marcado.procedimentos;
                const item = marcado.procedimentos[i];
                return (
                  <li key={i} className={`conf__item conf__item--largo${on ? ' conf__item--on' : ''}`}>
                    <label className="conf__check">
                      <input type="checkbox" checked={on}
                        onChange={() => alternar('procedimentos', i, {
                          nome: x.nome, data: x.data ?? e.dataDoRegistro ?? hoje(), resultado: x.resultado ?? '',
                        })} />
                      <span>{x.nome}</span>
                    </label>
                    {on && item && (
                      <div className="conf__campos conf__campos--empilha">
                        <input type="date" className="conf__data" value={item.data}
                          onChange={(ev) => editar('procedimentos', i, 'data', ev.target.value)} />
                        <textarea rows={3} value={item.resultado}
                          onChange={(ev) => editar('procedimentos', i, 'resultado', ev.target.value)} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {e.culturas.length > 0 && (
          <section className="conf">
            <h2 className="conf__titulo">Culturas</h2>
            <ul className="conf__lista">
              {e.culturas.map((x, i) => {
                const on = i in marcado.culturas;
                const item = marcado.culturas[i];
                return (
                  <li key={i} className={`conf__item conf__item--largo${on ? ' conf__item--on' : ''}`}>
                    <label className="conf__check">
                      <input type="checkbox" checked={on}
                        onChange={() => alternar('culturas', i, {
                          material: x.material, coletadoEm: x.coletadoEm ?? e.dataDoRegistro ?? hoje(),
                          resultado: x.resultado ?? '',
                        })} />
                      <span>{x.material}</span>
                    </label>
                    {on && item && (
                      <div className="conf__campos conf__campos--empilha">
                        <input type="date" className="conf__data" value={item.coletadoEm}
                          onChange={(ev) => editar('culturas', i, 'coletadoEm', ev.target.value)} />
                        <input value={item.resultado} placeholder="vazio = aguardando resultado"
                          onChange={(ev) => editar('culturas', i, 'resultado', ev.target.value)} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {e.medicacoes.length > 0 && (
          <section className="conf">
            <h2 className="conf__titulo">Medicações</h2>
            <ul className="conf__lista">
              {e.medicacoes.map((x, i) => {
                const on = i in marcado.medicacoes;
                const item = marcado.medicacoes[i];
                return (
                  <li key={i} className={`conf__item${on ? ' conf__item--on' : ''}`}>
                    <label className="conf__check">
                      <input type="checkbox" checked={on}
                        onChange={() => alternar('medicacoes', i, {
                          nome: x.nome, inicioEm: x.inicioEm ?? e.dataDoRegistro ?? hoje(),
                        })} />
                      <span>{x.nome}{x.dose ? ` · ${x.dose}` : ''}</span>
                    </label>
                    {on && item && (
                      <input type="date" className="conf__data" value={item.inicioEm}
                        onChange={(ev) => editar('medicacoes', i, 'inicioEm', ev.target.value)} />
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {e.resumo && (
          <section className="conf">
            <h2 className="conf__titulo">Evolução, como anotação</h2>
            <label className="conf__check">
              <input type="checkbox" checked={marcado.anotacao}
                onChange={() => setMarcado((m) => ({ ...m, anotacao: !m.anotacao }))} />
              <span>Guardar este texto na linha do tempo</span>
            </label>
            <p className="conf__resumo">{e.resumo}</p>
          </section>
        )}

        {e.observacoes && (
          <section className="conf">
            <h2 className="conf__titulo">O modelo viu e não soube encaixar</h2>
            <p className="conf__resumo conf__resumo--nota">{e.observacoes}</p>
          </section>
        )}

        <div className="importar__acoes">
          <button className="botao" onClick={aplicar} disabled={enviando || (total === 0 && !marcado.anotacao)}>
            {enviando ? 'Lançando…' : `Lançar ${total} ${total === 1 ? 'item' : 'itens'}`}
          </button>
          <button className="botao botao--fantasma" onClick={() => setAberto(null)}>Cancelar</button>
        </div>
      </main>
    );
  }

  // ------------------------------------------------------------- tela de envio
  return (
    <main className="importar">
      <header className="importar__topo">
        <div>
          <h1>Importar do prontuário</h1>
          <p className="importar__nota">
            Fotografe a tela do prontuário ou envie o PDF. O modelo lê e propõe o
            lançamento; <b>você confere antes de gravar</b>.
          </p>
        </div>
        <button className="botao botao--voltar" onClick={aoVoltar}>Voltar ao monitor</button>
      </header>

      {/* O aviso fica ANTES do botão de escolher arquivo, não depois: depois
          seria tarde. A imagem vai inteira para o provedor — não dá para
          tarjar pixel automaticamente sem arriscar apagar o exame junto. */}
      <p className="importar__privacidade">
        <b>Antes de fotografar:</b> enquadre só a parte do exame ou da evolução.
        O cabeçalho com nome, CNS, SES e data de nascimento não precisa entrar —
        a imagem é enviada inteira para o provedor do modelo.
      </p>

      {leituraOk && !leituraOk.disponivel && (
        <p className="importar__semChave">
          A leitura automática está <b>desligada</b>: falta a chave do OpenRouter
          no arquivo <code>.env</code> (<code>OPENROUTER_API_KEY</code>). Você
          ainda pode enviar e guardar os arquivos, e lançar à mão pela Ficha.
        </p>
      )}

      {feito && <p className="importar__feito">{feito}</p>}
      {erro && <p className="aviso-erro">{erro}</p>}

      <form className="importar__form" onSubmit={enviar}>
        <div className="campo">
          <label htmlFor="arq">Foto ou PDF</label>
          <input id="arq" type="file" accept="image/*,application/pdf" capture="environment"
            onChange={(ev) => setArquivo(ev.target.files?.[0] ?? null)} />
        </div>
        <div className="campo">
          <label htmlFor="tit">O que é <span>opcional</span></label>
          <div className="comDitado">
            <input id="tit" value={titulo} placeholder="Evolução de 28/08"
              onChange={(ev) => setTitulo(ev.target.value)} />
            <Ditar valor={titulo} aoMudar={setTitulo} rotulo="ditar o título" />
          </div>
        </div>
        <button className="botao" type="submit" disabled={!arquivo || enviando}>
          {enviando ? 'Enviando…' : 'Enviar'}
        </button>
      </form>

      <section>
        <h2 className="conf__titulo">Enviados</h2>
        {documentos.length === 0 && <p className="vazio">Nenhum documento enviado ainda.</p>}
        <ul className="importar__lista">
          {documentos.map((d) => (
            <li key={d.id} className={`importar__doc importar__doc--${d.situacao}`}>
              <div>
                <b>{d.titulo}</b>
                <small>
                  {quandoBr(d.criadoEm)} · {emKb(d.bytes)}
                  {d.modelo && ` · lido por ${d.modelo}`}
                  {d.aplicadoEm && ` · lançado em ${dataBr(new Date(d.aplicadoEm))}`}
                </small>
                {d.falha && <small className="importar__falha">{d.falha}</small>}
              </div>

              <div className="importar__docAcoes">
                <button className="botao botao--fantasma" onClick={() => setVendo(d)}>Ver</button>
                {d.situacao === 'lido' && d.extracao && (
                  <button className="botao" onClick={() => abrirParaConferir(d)}>Conferir</button>
                )}
                {d.situacao !== 'aplicado' && leituraOk?.disponivel && (
                  <button className="botao botao--fantasma" onClick={() => ler(d)} disabled={lendo === d.id}>
                    {lendo === d.id ? 'Lendo…' : d.situacao === 'erro' ? 'Tentar de novo' : 'Ler com IA'}
                  </button>
                )}
                {d.situacao === 'aplicado' && <span className="importar__ok">lançado ✓</span>}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <p className="importar__rodape">
        O catálogo reconhece {MARCADORES.length} marcadores de laboratório
        {acharMarcador('pcr') ? ', incluindo PCR, creatinina, gasometria e hemograma' : ''}.
        O que vier fora dele aparece na conferência, mas não pode ser lançado como exame.
      </p>
    </main>
  );
}
